import type { PreferenceStorage } from '../audio/sound-store';

export type BackgroundStyle = 'v1' | 'v2' | 'v3';

export const BACKGROUND_STYLES: readonly BackgroundStyle[] = ['v1', 'v2', 'v3'];

const STYLE_KEY = 'background';
/** Cheapest of the three since the planes became composited layers: its 1024px tile repeats six times across a 1886px viewport where V2's 256px tile repeats twenty-four, and the compositor pays per tile. */
const DEFAULT_STYLE: BackgroundStyle = 'v3';

const LEGACY_NAMES: Readonly<Record<string, BackgroundStyle>> = { old: 'v1', new: 'v2' };

export interface BackgroundStoreOptions {
  readonly storage?: PreferenceStorage | null | undefined;
}

function defaultStorage(): PreferenceStorage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export class BackgroundStore {
  private readonly storage: PreferenceStorage | null;
  private styleValue: BackgroundStyle;

  constructor(options: BackgroundStoreOptions = {}) {
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.styleValue = this.parse(this.read());
  }

  get style(): BackgroundStyle {
    return this.styleValue;
  }

  set style(value: BackgroundStyle) {
    if (value === this.styleValue) return;
    this.styleValue = value;
    this.write(value);
  }

  private parse(stored: string | null): BackgroundStyle {
    if (stored === null) return DEFAULT_STYLE;
    const named = LEGACY_NAMES[stored];
    if (named !== undefined) return named;
    return BACKGROUND_STYLES.find((style) => style === stored) ?? DEFAULT_STYLE;
  }

  private read(): string | null {
    try {
      return this.storage?.getItem(STYLE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private write(value: BackgroundStyle): void {
    try {
      this.storage?.setItem(STYLE_KEY, value);
    } catch {
      // must not break the toggle the player just clicked
    }
  }
}

export const backgroundStore = new BackgroundStore();
